/**
 * Orderflows Trader analysis of footprint bars (from /api/v1/orderflow).
 *
 * A bar: { time, open, high, low, close, rows: [{ price, bid, ask }] ascending, volume, buy,
 * sell, delta, maxDelta, minDelta, poc, vah, val, sweepUp, sweepDown }.
 * "bid" = volume that traded at the bid (selling), "ask" = at the ask (buying). On FX these
 * are tick-volume estimates (see backend/app/services/orderflow.py).
 *
 * GoCharting documents 19 of its 21 Orderflows Trader signals only loosely and gives few
 * thresholds, so the numbers below are this app's defaults (all adjustable). Buying/Selling
 * Tails and Orderflows Divergence are not described by GoCharting at all; they use the
 * common definitions noted next to them.
 */

export const ORDERFLOW_SIGNALS = [
  { key: "exhaustion", label: "Exhaustion Prints" },
  { key: "imbalanceReversal", label: "Imbalance Reversals" },
  { key: "ofRatio", label: "Orderflows Ratios" },
  { key: "slingshotPoc", label: "Slingshot POC" },
  { key: "marketWeakness", label: "Market Weakness" },
  { key: "ofSequencing", label: "Orderflows Sequencing" },
  { key: "marketSweep", label: "Market Sweep" },
  { key: "prominentPoc", label: "Prominent POCs" },
  { key: "zeroPrint", label: "Zero Prints" },
  { key: "inverseImbalance", label: "Inverse Imbalance" },
  { key: "multipleImbalance", label: "Multiple Imbalances" },
  { key: "stackedImbalance", label: "Stacked Imbalance" },
  { key: "tails", label: "Buying/Selling Tails" },
  { key: "deltaDivergence", label: "Delta Divergence" },
  { key: "valueArea", label: "Value Area" },
  { key: "engulfingVA", label: "Engulfing Value Area" },
  { key: "deltaVolume", label: "Delta/Volume" },
  { key: "smallMinMaxDelta", label: "Small Min/Max Delta" },
  { key: "extremeDelta", label: "Extreme Delta" },
  { key: "thinPrints", label: "Thin Prints" },
  { key: "ofDivergence", label: "Orderflows Divergence" },
];

/** Signals switched on when the Orderflows Trader is first added (the rest are one click away). */
export const ORDERFLOW_DEFAULT_ON = [
  "imbalanceReversal",
  "slingshotPoc",
  "prominentPoc",
  "multipleImbalance",
  "stackedImbalance",
  "deltaDivergence",
  "valueArea",
  "engulfingVA",
  "deltaVolume",
  "smallMinMaxDelta",
  "extremeDelta",
  "thinPrints",
];

export const ORDERFLOW_DEFAULTS = {
  imbalancePct: 300, // diagonal ask vs bid (or bid vs ask) must be >= this %
  imbalanceMin: 2, // ...and the bigger side at least this many ticks
  stackCount: 3, // stacked imbalance: consecutive levels
  exhaustionMax: 2, // exhaustion print: volume at the bar edge at most this
  thinMax: 3, // thin print: total volume of an inside level at most this
  sequencingLevels: 4, // levels of steadily growing aggressive volume
  prominentLookback: 20, // bars to compare volume against (0 = all loaded bars)
  prominentMult: 2, // bar volume >= mult x average volume
  divergenceLookback: 5, // bars back for Orderflows Divergence
  sweepMin: 1, // quote jumps across >= N rows (server "sweep" setting) to flag a sweep
};

export const OF_COLORS = {
  bull: "#22c55e",
  bear: "#ef4444",
  cyan: "#22d3ee",
  magenta: "#e879f9",
  purple: "#a855f7",
  blue: "#3b82f6",
  darkRed: "#991b1b",
  gray: "#94a3b8",
  yellow: "#facc15",
};

const dirOf = (b) => (b.close > b.open ? "up" : b.close < b.open ? "down" : "doji");

/** Diagonal imbalances (how footprint tools compare buying vs selling one level apart). */
function imbalances(rows, pct, minVol) {
  const r = pct / 100;
  return rows.map((row, i) => {
    const below = rows[i - 1];
    const above = rows[i + 1];
    const buy =
      row.ask >= minVol && (below ? (below.bid === 0 ? row.ask >= minVol : row.ask >= r * below.bid) : false);
    const sell =
      row.bid >= minVol && (above ? (above.ask === 0 ? row.bid >= minVol : row.bid >= r * above.ask) : false);
    return { buy, sell };
  });
}

const longestRun = (flags) => {
  let best = { len: 0, end: -1 };
  let cur = 0;
  flags.forEach((f, i) => {
    cur = f ? cur + 1 : 0;
    if (cur > best.len) best = { len: cur, end: i };
  });
  return best;
};

/**
 * Returns the bars with { dir, cells, stats, signals, zones } added.
 * cells[i]: { price, bid, ask, buyImb, sellImb, thin, inValueArea, poc }
 * signals: [{ key, bias: "bull" | "bear" | "info", text, color, position?: "above" | "below" }]
 * paint: { poc?: color, candle?: color, box?: color, line?: color, maxDeltaColor?, minDeltaColor?, deltaColor? }
 */
export function analyzeOrderflow(bars, options = {}) {
  const o = { ...ORDERFLOW_DEFAULTS, ...options };
  const out = [];
  let cvd = 0;
  bars.forEach((bar, bi) => {
    const rows = Array.isArray(bar.rows) ? bar.rows : [];
    const dir = dirOf(bar);
    const imb = imbalances(rows, o.imbalancePct, o.imbalanceMin);
    const vols = rows.map((r) => r.bid + r.ask);
    const volume = Number.isFinite(bar.volume) ? bar.volume : vols.reduce((a, b) => a + b, 0);
    const delta = Number.isFinite(bar.delta) ? bar.delta : rows.reduce((a, r) => a + r.ask - r.bid, 0);
    cvd += delta;
    const n = rows.length;
    const cells = rows.map((r, i) => ({
      price: r.price,
      bid: r.bid,
      ask: r.ask,
      buyImb: imb[i].buy,
      sellImb: imb[i].sell,
      thin: i >= 2 && i <= n - 3 && vols[i] <= o.thinMax,
      inValueArea: bar.val != null && bar.vah != null && r.price >= bar.val - 1e-12 && r.price <= bar.vah + 1e-12,
      poc: bar.poc != null && Math.abs(r.price - bar.poc) < 1e-12,
    }));
    const signals = [];
    const paint = {};
    const add = (key, bias, text, extra = {}) =>
      signals.push({
        key,
        bias,
        text,
        color: bias === "bull" ? OF_COLORS.bull : bias === "bear" ? OF_COLORS.bear : OF_COLORS.gray,
        position: bias === "bear" ? "above" : "below",
        ...extra,
      });

    if (n) {
      const top = rows[n - 1];
      const bottom = rows[0];
      const buyCount = imb.filter((x) => x.buy).length;
      const sellCount = imb.filter((x) => x.sell).length;

      // 1. Exhaustion prints: almost nothing traded at the extreme in the bar's direction's end
      if (dir === "up" && bottom.bid <= o.exhaustionMax && n > 1) add("exhaustion", "bull", "EX", { cellIndex: 0, side: "bid" });
      if (dir === "down" && top.ask <= o.exhaustionMax && n > 1) add("exhaustion", "bear", "EX", { cellIndex: n - 1, side: "ask" });

      // 2. Imbalance reversals: imbalance against the bar at its edge (trapped traders)
      const edgeSell = imb.slice(0, 2).some((x) => x.sell);
      const edgeBuy = imb.slice(-2).some((x) => x.buy);
      if (dir === "up" && edgeSell) add("imbalanceReversal", "bull", "IR", { color: OF_COLORS.magenta });
      if (dir === "down" && edgeBuy) add("imbalanceReversal", "bear", "IR", { color: OF_COLORS.cyan });

      // 3. Orderflows ratios (next level in / edge level); >= 30 exhaustion, 0-0.69 defense
      if (n >= 2) {
        const low = bottom.bid > 0 ? rows[1].bid / bottom.bid : null;
        const high = top.ask > 0 ? rows[n - 2].ask / top.ask : null;
        const tag = (v) => (v == null ? null : v >= 30 ? "exhaustion" : v <= 0.69 ? "defense" : null);
        const lowTag = tag(low);
        const highTag = tag(high);
        // exhaustion or defense at the low both point up; at the high both point down
        if (lowTag) add("ofRatio", "bull", `R ${low.toFixed(2)}`, { ratio: low, at: "low", kind: lowTag });
        if (highTag) add("ofRatio", "bear", `R ${high.toFixed(2)}`, { ratio: high, at: "high", kind: highTag });
      }

      // 9. Zero prints: no two-way trade at the edge
      if (bottom.bid === 0 && n > 1) add("zeroPrint", "bull", "0", { cellIndex: 0, side: "bid" });
      if (top.ask === 0 && n > 1) add("zeroPrint", "bear", "0", { cellIndex: n - 1, side: "ask" });

      // 4. Slingshot POC: heavy volume at one end, bar closes away from it
      const pocIdx = cells.findIndex((c) => c.poc);
      if (pocIdx >= 0 && n >= 4) {
        const pos = pocIdx / (n - 1);
        const closePos = (bar.close - bar.low) / Math.max(bar.high - bar.low, 1e-12);
        if (pos <= 0.25 && dir === "up" && closePos >= 0.5) {
          paint.poc = OF_COLORS.bull;
          add("slingshotPoc", "bull", "SS");
        } else if (pos >= 0.75 && dir === "down" && closePos <= 0.5) {
          paint.poc = OF_COLORS.bear;
          add("slingshotPoc", "bear", "SS");
        }
      }

      // 6. Orderflows sequencing: aggressive volume growing level after level
      const growing = (vals) => {
        let run = 1;
        for (let i = 1; i < vals.length; i += 1) {
          run = vals[i] > vals[i - 1] && vals[i] > 0 ? run + 1 : 1;
          if (run >= o.sequencingLevels) return true;
        }
        return false;
      };
      if (dir === "up" && growing(rows.map((r) => r.ask))) add("ofSequencing", "bull", "SEQ");
      if (dir === "down" && growing(rows.map((r) => r.bid).reverse())) add("ofSequencing", "bear", "SEQ");

      // 7. Market sweep: a single quote jump crossed several rows (counted by the server)
      if ((bar.sweepUp ?? 0) >= o.sweepMin) add("marketSweep", "bull", "SW↑");
      if ((bar.sweepDown ?? 0) >= o.sweepMin) add("marketSweep", "bear", "SW↓");

      // 10. Inverse imbalance: imbalances against the bar inside its body half
      const half = Math.floor(n / 2);
      if (dir === "up" && imb.slice(0, half).some((x) => x.sell)) add("inverseImbalance", "bull", "▲", { shape: "triangle" });
      if (dir === "down" && imb.slice(n - half).some((x) => x.buy)) add("inverseImbalance", "bear", "▼", { shape: "triangle" });

      // 11. Multiple imbalances
      if (buyCount + sellCount >= 2) {
        paint.box = OF_COLORS.purple;
        add("multipleImbalance", "info", "MI", { color: OF_COLORS.purple });
      }

      // 12. Stacked imbalances (zone from the stack until price trades back through it)
      const buyRun = longestRun(imb.map((x) => x.buy));
      const sellRun = longestRun(imb.map((x) => x.sell));
      if (dir === "up" && buyRun.len >= o.stackCount) {
        const loP = rows[buyRun.end - buyRun.len + 1].price;
        add("stackedImbalance", "bull", `SI${buyRun.len}`, { zone: { from: loP, to: rows[buyRun.end].price } });
      }
      if (dir === "down" && sellRun.len >= o.stackCount) {
        const loP = rows[sellRun.end - sellRun.len + 1].price;
        add("stackedImbalance", "bear", `SI${sellRun.len}`, { zone: { from: loP, to: rows[sellRun.end].price } });
      }

      // 13. Tails (common definition): long wick of thin volume rejected by the close
      const range = Math.max(bar.high - bar.low, 1e-12);
      const avgRow = volume / n;
      const lowerWick = Math.min(bar.open, bar.close) - bar.low;
      const upperWick = bar.high - Math.max(bar.open, bar.close);
      if (lowerWick / range >= 0.4 && n >= 3 && vols[0] < 0.5 * avgRow) add("tails", "bull", "BT");
      if (upperWick / range >= 0.4 && n >= 3 && vols[n - 1] < 0.5 * avgRow) add("tails", "bear", "ST");

      // 20. Thin prints inside the bar
      if (cells.some((c) => c.thin)) add("thinPrints", "info", "TP");
    }

    // 14. Delta divergence: delta against the candle
    if (dir === "up" && delta < 0) {
      paint.line = OF_COLORS.bull;
      add("deltaDivergence", "bull", "DD");
    } else if (dir === "down" && delta > 0) {
      paint.line = OF_COLORS.bear;
      add("deltaDivergence", "bear", "DD");
    }

    // 15/16. Value area and engulfing value area
    if (bar.vah != null) add("valueArea", "info", "VA", { silent: true });
    const prev = bars[bi - 1];
    if (prev && bar.vah != null && prev.vah != null && bar.vah >= prev.vah && bar.val <= prev.val && (bar.vah > prev.vah || bar.val < prev.val)) {
      if (dir === "up") {
        paint.candle = OF_COLORS.blue;
        add("engulfingVA", "bull", "EVA", { color: OF_COLORS.blue });
      } else if (dir === "down") {
        paint.candle = OF_COLORS.darkRed;
        add("engulfingVA", "bear", "EVA", { color: OF_COLORS.darkRed });
      }
    }

    // 5. Market weakness: new extreme with fading pressure behind it
    if (prev) {
      if (bar.low < prev.low && bar.sell < prev.sell) add("marketWeakness", "bull", "MW");
      if (bar.high > prev.high && bar.buy < prev.buy) add("marketWeakness", "bear", "MW");
    }

    // 17-19. Delta statistics
    const maxDelta = bar.maxDelta ?? 0;
    const minDelta = bar.minDelta ?? 0;
    const stats = {
      volume,
      delta,
      maxDelta,
      minDelta,
      cumDelta: cvd,
      deltaPerVolume: volume ? delta / volume : 0,
    };
    if (maxDelta === 0 && volume > 0) {
      paint.maxDeltaColor = OF_COLORS.bear;
      add("smallMinMaxDelta", "bear", "sMax", { silent: true });
    }
    if (minDelta === 0 && volume > 0) {
      paint.minDeltaColor = OF_COLORS.bull;
      add("smallMinMaxDelta", "bull", "sMin", { silent: true });
    }
    if (delta > 0 && maxDelta > 0 && delta >= 0.95 * maxDelta) {
      paint.deltaColor = OF_COLORS.cyan;
      add("extremeDelta", "bull", "XD", { color: OF_COLORS.cyan, silent: true });
    } else if (delta < 0 && minDelta < 0 && delta <= 0.95 * minDelta) {
      paint.deltaColor = OF_COLORS.magenta;
      add("extremeDelta", "bear", "XD", { color: OF_COLORS.magenta, silent: true });
    }
    add("deltaVolume", "info", "", { silent: true });

    out.push({ ...bar, dir, cells, stats, signals, paint });
  });

  // 8. Prominent POC: unusually heavy bars; zone from the POC until price comes back to it
  out.forEach((b, i) => {
    const look = o.prominentLookback > 0 ? out.slice(Math.max(0, i - o.prominentLookback), i) : out.slice(0, i);
    if (look.length < 3 || b.poc == null) return;
    const avg = look.reduce((s, x) => s + x.stats.volume, 0) / look.length;
    if (b.stats.volume >= o.prominentMult * avg) {
      const bias = b.dir === "down" ? "bear" : "bull";
      if (!b.paint.poc) b.paint.poc = bias === "bull" ? OF_COLORS.cyan : OF_COLORS.magenta;
      let until = null;
      for (let j = i + 1; j < out.length; j += 1) {
        if (out[j].low <= b.poc && out[j].high >= b.poc) {
          until = out[j].time;
          break;
        }
      }
      b.signals.push({
        key: "prominentPoc",
        bias,
        text: "PP",
        color: bias === "bull" ? OF_COLORS.cyan : OF_COLORS.magenta,
        position: bias === "bear" ? "above" : "below",
        zone: { from: b.poc, to: b.poc, until },
      });
    }
  });

  // stacked-imbalance zones end when price trades back through them
  out.forEach((b, i) => {
    b.signals.forEach((s) => {
      if (s.key !== "stackedImbalance" || !s.zone) return;
      for (let j = i + 1; j < out.length; j += 1) {
        const crossed = s.bias === "bull" ? out[j].low < s.zone.from : out[j].high > s.zone.to;
        if (crossed) {
          s.zone.until = out[j].time;
          break;
        }
      }
    });
  });

  // 21. Orderflows divergence (common definition): price and cumulative delta disagree
  const L = o.divergenceLookback;
  out.forEach((b, i) => {
    if (i < L) return;
    const past = out[i - L];
    if (b.high > past.high && b.stats.cumDelta < past.stats.cumDelta) {
      b.signals.push({ key: "ofDivergence", bias: "bear", text: "DIV", color: OF_COLORS.bear, position: "above" });
    } else if (b.low < past.low && b.stats.cumDelta > past.stats.cumDelta) {
      b.signals.push({ key: "ofDivergence", bias: "bull", text: "DIV", color: OF_COLORS.bull, position: "below" });
    }
  });

  return out;
}

/** Chart markers for the enabled signal keys (signals that are drawn inside cells are skipped). */
export function orderflowMarkers(analyzed, enabled) {
  const markers = [];
  analyzed.forEach((b) => {
    b.signals.forEach((s) => {
      if (s.silent || !enabled.has(s.key)) return;
      markers.push({
        time: b.time,
        position: s.position === "above" ? "aboveBar" : "belowBar",
        shape: s.shape === "triangle" ? (s.position === "above" ? "arrowDown" : "arrowUp") : "circle",
        color: s.color,
        text: s.text,
        size: 0.6,
      });
    });
  });
  return markers;
}

/** Volume-at-price profile over the given bars: [{ price, bid, ask }] ascending, with POC/VA. */
export function volumeProfile(bars, share = 0.7) {
  const map = new Map();
  bars.forEach((b) =>
    (b.rows || []).forEach((r) => {
      const key = r.price.toFixed(10);
      const cur = map.get(key) || { price: r.price, bid: 0, ask: 0 };
      cur.bid += r.bid;
      cur.ask += r.ask;
      map.set(key, cur);
    })
  );
  const rows = [...map.values()].sort((a, b) => a.price - b.price);
  if (!rows.length) return { rows, poc: null, vah: null, val: null };
  const vols = rows.map((r) => r.bid + r.ask);
  const total = vols.reduce((a, b) => a + b, 0);
  let pocI = 0;
  vols.forEach((v, i) => {
    if (v > vols[pocI]) pocI = i;
  });
  let lo = pocI;
  let hi = pocI;
  let acc = vols[pocI];
  while (acc < share * total && (lo > 0 || hi < rows.length - 1)) {
    const up = hi < rows.length - 1 ? vols[hi + 1] : -1;
    const dn = lo > 0 ? vols[lo - 1] : -1;
    if (up >= dn) acc += vols[++hi];
    else acc += vols[--lo];
  }
  return { rows, poc: rows[pocI].price, vah: rows[hi].price, val: rows[lo].price };
}

const TPO_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXabcdefghijklmnopqrstuvwx";

/**
 * Market profile (TPO): for each UTC day, every price row touched in each period of
 * `periodMinutes` gets that period's letter. Returns [{ dayStart, rows: [{ price, letters }],
 * poc, vah, val, ibHigh, ibLow }].
 */
export function marketProfile(bars, rowSize, periodMinutes = 30, share = 0.7) {
  if (!rowSize || !bars.length) return [];
  const days = new Map();
  bars.forEach((b) => {
    const dayStart = Math.floor(b.time / 86400) * 86400;
    const letterIdx = Math.min(TPO_LETTERS.length - 1, Math.floor((b.time - dayStart) / (periodMinutes * 60)));
    const day = days.get(dayStart) || { dayStart, cells: new Map(), firstHour: [] };
    const lo = Math.floor(b.low / rowSize + 1e-9);
    const hi = Math.floor(b.high / rowSize + 1e-9);
    for (let k = lo; k <= hi; k += 1) {
      const set = day.cells.get(k) || new Set();
      set.add(TPO_LETTERS[letterIdx]);
      day.cells.set(k, set);
    }
    if (b.time - dayStart < 3600) day.firstHour.push(b);
    days.set(dayStart, day);
  });
  return [...days.values()].map((d) => {
    const keys = [...d.cells.keys()].sort((a, b) => a - b);
    const rows = keys.map((k) => ({
      price: +(k * rowSize).toFixed(10),
      letters: [...d.cells.get(k)].sort((a, b) => TPO_LETTERS.indexOf(a) - TPO_LETTERS.indexOf(b)).join(""),
    }));
    const prof = volumeProfile([{ rows: rows.map((r) => ({ price: r.price, bid: r.letters.length, ask: 0 })) }], share);
    return {
      dayStart: d.dayStart,
      rows,
      poc: prof.poc,
      vah: prof.vah,
      val: prof.val,
      ibHigh: d.firstHour.length ? Math.max(...d.firstHour.map((b) => b.high)) : null,
      ibLow: d.firstHour.length ? Math.min(...d.firstHour.map((b) => b.low)) : null,
    };
  });
}

/** A "nice" step (1, 2, 2.5 or 5 x a power of ten) at or above `x`. */
function niceStep(x) {
  if (!(x > 0)) return 0;
  const mag = 10 ** Math.floor(Math.log10(x));
  for (const m of [1, 2, 2.5, 5, 10]) {
    if (m * mag >= x - 1e-15) return +(m * mag).toPrecision(6);
  }
  return 10 * mag;
}

/**
 * Market Profile (TPO) built from the chart's own candles, one profile per trading session.
 * Every price row a candle touched in a TPO period gets that period's letter (A = first period
 * of the session). Works on any candle timeframe up to the TPO period.
 *
 * options: periodMinutes (30), sessionStartHour (UTC hour the trading day starts; FX brokers
 * use 21:00 or 22:00 UTC), rowSize (0 = automatic, about `rowsPerSession` rows a session),
 * valueAreaShare (0.7). Returns [{ start, firstTime, lastTime, rowSize, rows: [{price, letters}],
 * poc, vah, val, ibHigh, ibLow, high, low }] oldest first.
 */
export function marketProfileFromCandles(candles, options = {}) {
  const periodMinutes = Math.max(1, options.periodMinutes ?? 30);
  const sessionStartHour = ((options.sessionStartHour ?? 0) % 24 + 24) % 24;
  const share = options.valueAreaShare ?? 0.7;
  const rowsPerSession = options.rowsPerSession ?? 40;
  if (!Array.isArray(candles) || !candles.length) return [];
  const off = sessionStartHour * 3600;
  const sessions = new Map();
  candles.forEach((c) => {
    if (!Number.isFinite(c?.time) || !Number.isFinite(c.high) || !Number.isFinite(c.low)) return;
    const start = Math.floor((c.time - off) / 86400) * 86400 + off;
    let sess = sessions.get(start);
    if (!sess) {
      sess = { start, candles: [], high: -Infinity, low: Infinity };
      sessions.set(start, sess);
    }
    sess.candles.push(c);
    sess.high = Math.max(sess.high, c.high);
    sess.low = Math.min(sess.low, c.low);
  });
  const list = [...sessions.values()].sort((a, b) => a.start - b.start);
  let rowSize = options.rowSize > 0 ? options.rowSize : 0;
  if (!rowSize) {
    const ranges = list.map((x) => x.high - x.low).filter((r) => r > 0).sort((a, b) => a - b);
    const median = ranges.length ? ranges[Math.floor(ranges.length / 2)] : 0;
    rowSize = niceStep(median / rowsPerSession) || 1e-5;
  }
  const periodSec = periodMinutes * 60;
  return list.map((sess) => {
    const cells = new Map();
    const ib = [];
    sess.candles.forEach((c) => {
      const idx = Math.min(TPO_LETTERS.length - 1, Math.floor((c.time - sess.start) / periodSec));
      const letter = TPO_LETTERS[idx];
      if (c.time - sess.start < 3600) ib.push(c);
      const lo = Math.floor(c.low / rowSize + 1e-9);
      const hi = Math.floor(c.high / rowSize + 1e-9);
      for (let k = lo; k <= hi; k += 1) {
        const set = cells.get(k) || new Set();
        set.add(letter);
        cells.set(k, set);
      }
    });
    const keys = [...cells.keys()].sort((a, b) => a - b);
    const rows = keys.map((k) => ({
      price: +(k * rowSize).toFixed(10),
      letters: [...cells.get(k)].sort((a, b) => TPO_LETTERS.indexOf(a) - TPO_LETTERS.indexOf(b)).join(""),
    }));
    const prof = volumeProfile([{ rows: rows.map((r) => ({ price: r.price, bid: r.letters.length, ask: 0 })) }], share);
    return {
      start: sess.start,
      firstTime: sess.candles[0].time,
      lastTime: sess.candles[sess.candles.length - 1].time,
      rowSize,
      rows,
      poc: prof.poc,
      vah: prof.vah,
      val: prof.val,
      ibHigh: ib.length ? Math.max(...ib.map((c) => c.high)) : null,
      ibLow: ib.length ? Math.min(...ib.map((c) => c.low)) : null,
      high: sess.high,
      low: sess.low,
    };
  });
}

/** Session (UTC day) VWAP of buying volume and of selling volume, from footprint rows. */
export function buySellVwap(bars) {
  const buy = [];
  const sell = [];
  let day = null;
  let bv = 0;
  let bpv = 0;
  let sv = 0;
  let spv = 0;
  bars.forEach((b) => {
    const d = Math.floor(b.time / 86400);
    if (d !== day) {
      day = d;
      bv = 0;
      bpv = 0;
      sv = 0;
      spv = 0;
    }
    (b.rows || []).forEach((r) => {
      bv += r.ask;
      bpv += r.ask * r.price;
      sv += r.bid;
      spv += r.bid * r.price;
    });
    buy.push(bv > 0 ? { time: b.time, value: bpv / bv } : { time: b.time });
    sell.push(sv > 0 ? { time: b.time, value: spv / sv } : { time: b.time });
  });
  return { buy, sell };
}
